import type { AnchorHTMLAttributes, MouseEvent, ReactNode } from 'react'

import { navigateTo } from '../../../common/frontend/route-utils.js'
import { isExternalUrl } from '../../../common/utils/url-utils.js'

export interface LinkProps extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> {
    href: string
    children: ReactNode
    // Render a plain anchor with NO client-side intercept (auth endpoints, the
    // separate /admin SPA, off-origin links). Off-origin targets open in a new
    // tab by default; same-origin (e.g. /admin) stays same-tab. Override via target.
    external?: boolean
    // Visual active state + aria-current. Deriving "active" is the caller's job.
    isActive?: boolean
}

// The one client-side link primitive. Internal links navigate via navigateTo
// (no full page reload) while preserving native modifier/middle-click
// "open in new tab" behavior. External links fall through to the browser.
export const Link = ({
    href,
    children,
    external,
    isActive,
    className,
    onClick,
    target,
    rel,
    ...rest
}: LinkProps) => {
    const classes = [className, isActive && 'is-active'].filter(Boolean).join(' ') || undefined
    const ariaCurrent = isActive ? 'page' : undefined

    if (external) {
        const offOrigin = isExternalUrl(href)
        return (
            <a
                href={href}
                className={classes}
                aria-current={ariaCurrent}
                target={target ?? (offOrigin ? '_blank' : undefined)}
                rel={rel ?? (offOrigin ? 'noopener noreferrer' : undefined)}
                onClick={onClick}
                {...rest}
            >
                {children}
            </a>
        )
    }

    const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
        onClick?.(event)
        // Let the browser handle modified / non-primary clicks (open in new tab,
        // download, etc.) and anything a consumer onClick already handled.
        if (
            event.defaultPrevented ||
            event.button !== 0 ||
            event.metaKey ||
            event.ctrlKey ||
            event.shiftKey ||
            event.altKey
        ) {
            return
        }
        event.preventDefault()
        navigateTo(href)
    }

    return (
        <a
            href={href}
            className={classes}
            aria-current={ariaCurrent}
            target={target}
            rel={rel}
            onClick={handleClick}
            {...rest}
        >
            {children}
        </a>
    )
}
